import React, { useEffect, useState } from "react";
import { Church, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { imageVariant } from "@/lib/mediaUrl";

let cache = null;
// Églises que le membre peut gérer (responsable, ou toutes pour un admin) — chargées une fois par session.
export const loadMyChurches = () => {
  if (!cache) cache = api.get("/churches/mine").then(({ data }) => data.churches || []).catch(() => { cache = null; return []; });
  return cache;
};

// Rattacher un direct / une rediffusion à la page d'une église gérée par le membre
// (« Directs & rediffusions » de l'église + abonnés prévenus). Utilisé depuis le menu ⋯ du fil et « Mes directs ».
export const AttachLiveToChurchDialog = ({ open, onOpenChange, liveId, postId, currentChurchId, onAttached }) => {
  const { t } = useApp();
  const [churches, setChurches] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { if (open) loadMyChurches().then(setChurches); }, [open]);

  const attach = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/churches/${c.id}/attach-live`, { live_id: liveId || undefined, post_id: postId || undefined });
      toast.success(t("live.attach_church_done", "Rattaché à {church} ✅").replace("{church}", c.name));
      onAttached?.(c);
      onOpenChange(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(""); }
  };

  const list = (churches || []).filter((c) => !q.trim() || (c.name || "").toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[95]" data-testid="attach-church-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Church size={18} style={{ color: "var(--yalola-primary)" }} /> {t("live.attach_church_title", "Rattacher à une page d'église")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("live.attach_church_hint", "Le direct ou la rediffusion apparaîtra dans « Directs & rediffusions » de l'église choisie et ses abonnés seront prévenus.")}</p>
        {churches === null && <div className="py-8 grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>}
        {churches !== null && churches.length === 0 && (
          <div className="py-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="attach-church-empty">{t("live.attach_church_none", "Tu ne gères aucune église vérifiée pour l'instant.")}</div>
        )}
        {churches !== null && churches.length > 6 && (
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("live.attach_church_search", "Rechercher une église…")} className="pl-9 rounded-full" data-testid="attach-church-search" />
          </div>
        )}
        <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="attach-church-list">
          {list.map((c) => {
            const here = c.id === currentChurchId;
            return (
              <div key={c.id} className="flex items-center gap-3 rounded-xl border p-2" style={{ borderColor: "var(--yalola-border)", opacity: here ? 0.6 : 1 }} data-testid={`attach-church-item-${c.id}`}>
                <div className="h-10 w-10 shrink-0 rounded-full overflow-hidden grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}>
                  {c.logo_url ? <img src={imageVariant(c.logo_url, 48)} alt="" className="h-full w-full object-cover" /> : <Church size={18} style={{ color: "var(--yalola-primary)" }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  {c.city && <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{c.city}</div>}
                </div>
                <Button size="sm" className={`rounded-full shrink-0 ${here ? "" : "pill-primary"}`} variant={here ? "outline" : "default"} disabled={here || !!busy} onClick={() => attach(c)} data-testid={`attach-church-do-${c.id}`}>
                  {busy === c.id ? <Loader2 size={14} className="animate-spin" /> : here ? t("live.attach_church_current", "Déjà sur cette page") : t("church.attach_action", "Rattacher")}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
